import { TFunction } from 'i18next'
import {
  CellContext,
  HeaderContext,
  SortingFnOption,
} from '@tanstack/react-table'

import CharacterAvatar from '../../components/CharacterAvatar'
import Timestamp from '../../components/Timestamp'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ChevronDown, Crown, Edit } from 'lucide-react'
import { Link } from 'react-router-dom'
import CharacterClassIcon from '../../components/CharacterClassIcon'
import TeamCard from '../../components/TeamCard'
import { genericSorting } from './util'
import { Difficulty } from 'app/frontend/types/custom'
import { difficulties } from 'app/frontend/types/custom/Difficulty'
import { capitalize } from 'lodash'
import CharacterExternalIconLink from '../../components/CharacterExternalIconLink'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { cn } from '@/lib/utils'

export const name = {
  accessorKey: 'name',
  id: 'name',
  size: 175,
  ...genericSorting<wowaudit.CharacterReference>('text', 'Name'),
  cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
    return (
      <div className='flex'>
        <Link
          to={info.row.original.path}
          className='inline-flex gap-2 items-center'
        >
          <CharacterAvatar character={info.row.original} className='h-6 w-6' />

          {info.row.original.name}
        </Link>
      </div>
    )
  },
}

export const characterClass = {
  accessorKey: 'characterClass',
  id: 'characterClass',
  size: 175,
  ...genericSorting<wowaudit.CharacterReference>('text', 'Class'),
  cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
    return (
      <span
        className={cn(
          'flex gap-2 items-center',
          classColours[info.row.original.characterClass].text,
        )}
      >
        <CharacterClassIcon characterClass={info.row.original.characterClass} />
        <span>{info.row.original.characterClass}</span>
      </span>
    )
  },
}

export const realm = {
  accessorKey: 'realm.name',
  id: 'realm',
  ...genericSorting<wowaudit.CharacterReference>('text', 'Realm'),
  cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
    return info.row.original.realm.nameWithRegion
  },
}

export const level = {
  accessorKey: 'level',
  id: 'level',
  size: 75,
  ...genericSorting<wowaudit.CharacterReference>('alphanumeric', 'Level', true),
}

export const colorForParse = (parse: number) => {
  if (parse <= 24) return 'text-quality-poor'
  if (parse <= 49) return 'text-quality-uncommon'
  if (parse <= 74) return 'text-quality-rare'
  if (parse <= 94) return 'text-quality-epic'
  if (parse <= 98) return 'text-quality-legendary'
  if (parse === 99) return 'text-[#e268a8]'
  return 'text-quality-artifact'
}

export const getRaidProgressParse = (
  progress?: wowaudit.CharacterDetails['currentRaidProgression'],
  difficulty: Difficulty = 'mythic',
): { difficulty: Difficulty; element: React.ReactNode | string } => {
  if (!progress) return { difficulty, element: '-' }

  const availableParses = Object.values(progress[difficulty]).filter(
    (curr) => curr.parse !== null && curr.parse > 0,
  )

  if (availableParses.length === 0) {
    const nextDifficultyIndex = difficulties.indexOf(difficulty) + 1
    if (nextDifficultyIndex < difficulties.length) {
      return getRaidProgressParse(progress, difficulties[nextDifficultyIndex])
    }

    return { difficulty, element: '-' }
  }

  const averageParse =
    availableParses.reduce((acc, curr) => acc + (curr.parse || 0), 0) /
    availableParses.length

  return {
    difficulty,
    element: (
      <span className={colorForParse(averageParse)}>
        {Math.round(averageParse)}
      </span>
    ),
  }
}

export const raidParse = <T extends { id: number }>(
  extendedDetails: Record<string, wowaudit.CharacterDetails>,
  getEntityId: (entity: T) => number | undefined,
  defaultDifficulty: Difficulty = 'mythic',
) => {
  return {
    accessorFn: (row: T) => {
      const details = extendedDetails[getEntityId(row)?.toString() ?? '']
      return details?.mythicRating || 0
    },
    id: 'raidParse',
    size: 70,
    ...genericSorting<T>('alphanumeric', 'Parse', false),
    cell: (info: CellContext<T, unknown>) => {
      const entityId = getEntityId(info.row.original)
      if (!entityId) return '-'
      const details = extendedDetails[entityId.toString()]
      if (!details) return '-'

      return (
        <div
          className={`transition-opacity duration-300 w-8 ${
            details ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {
            getRaidProgressParse(
              details.currentRaidProgression,
              defaultDifficulty,
            ).element
          }
        </div>
      )
    },
  }
}

export const getRaidProgressSummary = (
  progress: wowaudit.CharacterDetails['currentRaidProgression'],
  difficulty: Difficulty = 'mythic',
): { difficulty: Difficulty | null; element: React.ReactNode | string } => {
  const kills = Object.values(progress[difficulty]).reduce(
    (acc, curr) => acc + (curr.kills > 0 ? 1 : 0),
    0,
  )

  if (kills === 0) {
    const nextDifficultyIndex = difficulties.indexOf(difficulty) + 1
    if (nextDifficultyIndex < difficulties.length) {
      return getRaidProgressSummary(progress, difficulties[nextDifficultyIndex])
    }

    return { difficulty: null, element: '-' }
  }

  const totalSize = Object.values(progress[difficulty]).length
  return {
    difficulty,
    element: (
      <span className='flex items-center gap-0.5'>
        <span>{kills}</span>
        <span>/</span>
        <span>{totalSize}</span>
        <span className='ml-1'>{capitalize(difficulty[0])}</span>
      </span>
    ),
  }
}

export const raidProgress = <T extends { id: number }>(
  extendedDetails: Record<string, wowaudit.CharacterDetails>,
  getEntityId: (entity: T) => number | undefined,
  defaultDifficulty: Difficulty = 'mythic',
) => {
  return {
    accessorFn: (row: T) => {
      const details = extendedDetails[getEntityId(row)?.toString() ?? '']
      return details?.mythicRating || 0
    },
    id: 'raidProgress',
    size: 90,
    ...genericSorting<T>('alphanumeric', 'Progress', true),
    cell: (info: CellContext<T, unknown>) => {
      const entityId = getEntityId(info.row.original)
      if (!entityId) return '-'
      const details = extendedDetails[entityId.toString()]
      if (!details) return '-'

      return (
        <div
          className={`transition-opacity duration-300 w-8 ${
            details ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {
            getRaidProgressSummary(
              details.currentRaidProgression,
              defaultDifficulty,
            ).element
          }
        </div>
      )
    },
  }
}

export const mythicRating = <T extends { id: number }>(
  extendedDetails: Record<string, wowaudit.CharacterDetails>,
  getEntityId: (entity: T) => number | undefined,
) => {
  return {
    accessorFn: (row: T) => {
      const details = extendedDetails[getEntityId(row)?.toString() ?? '']
      return details?.mythicRating || 0
    },
    id: 'mythicRating',
    size: 95,
    ...genericSorting<T>('alphanumeric', 'M+ score', true),
    cell: (info: CellContext<T, unknown>) => {
      const entityId = getEntityId(info.row.original)
      if (!entityId) return '-'
      const details = extendedDetails[entityId.toString()]

      return (
        <div
          className={`transition-opacity duration-300 ${
            details ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ color: details?.mythicRatingColor?.overall || 'inherit' }}
        >
          {details?.mythicRating || '-'}
        </div>
      )
    },
  }
}

export const itemLevel = <T extends { id: number }>(
  extendedDetails: Record<string, wowaudit.CharacterDetails>,
  getEntityId: (entity: T) => number | undefined,
) => {
  return {
    accessorFn: (row: T) => {
      const details = extendedDetails[getEntityId(row)?.toString() ?? '']
      return details?.maxItemLevel || 0
    },
    id: 'itemLevel',
    size: 75,
    ...genericSorting<T>('alphanumeric', 'iLvl', true),
    cell: (info: CellContext<T, unknown>) => {
      const entityId = getEntityId(info.row.original)
      const details = extendedDetails[entityId?.toString() ?? '']

      return (
        <div
          className={`transition-opacity duration-300 w-8 ${
            details ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {details?.maxItemLevel || '-'}
        </div>
      )
    },
  }
}

export const teams = (
  t: TFunction,
  extendedDetails: Record<string, wowaudit.Character>,
) => {
  return {
    id: 'teams',
    size: 110,
    ...genericSorting<wowaudit.CharacterReference>(
      'alphanumeric',
      'Teams',
      true,
    ),
    cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
      const extendedCharacter = extendedDetails[info.row.original.id]
      const teams = extendedCharacter?.teamReferences || []

      return (
        <div
          className={`transition-opacity duration-300 w-22 ${
            extendedCharacter ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {teams.length ? (
            <Popover>
              <PopoverTrigger className='flex items-center gap-2'>
                {t('profile.character-table.team-amount', {
                  count: teams.length,
                })}
                <ChevronDown size={12} />
              </PopoverTrigger>
              <PopoverContent align='end' className='text-sm'>
                {teams.map((team) => (
                  <TeamCard key={team.id} team={team} />
                ))}
              </PopoverContent>
            </Popover>
          ) : (
            <span className='italic opacity-70'>{t('shared.none')}</span>
          )}
        </div>
      )
    },
  }
}

export const guildRankLabel = (
  rankInGuild: number | undefined,
  guildRanks?: wowaudit.GuildRank[],
  guildMasterLabel = 'Guild Master',
  displayRankLabel?: (displayRank: number) => string,
) => {
  const displayRank = rankInGuild === undefined ? undefined : rankInGuild + 1
  const guildRank = guildRanks?.find(({ rank }) => rank === displayRank)

  if (guildRank && displayRank !== undefined)
    return displayRankLabel
      ? `${guildRank.name} (${displayRankLabel(displayRank)})`
      : guildRank.name

  return rankInGuild === 0
    ? guildMasterLabel
    : rankInGuild
    ? `Rank ${displayRank}`
    : '-'
}

export const detailedRankInGuild = (guildRanks?: wowaudit.GuildRank[]) => {
  return {
    accessorKey: 'rankInGuild',
    id: 'rankInGuild',
    size: 120,
    ...genericSorting<wowaudit.CharacterReference>(
      'alphanumeric',
      'Rank in guild',
      false,
    ),
    cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
      const label = guildRankLabel(
        info.row.original.rankInGuild,
        guildRanks,
        'Guild Master',
      )

      return (
        <span className='block truncate' title={label}>
          {label}
        </span>
      )
    },
  }
}

export const rankInGuild = (guildRanks?: wowaudit.GuildRank[]) => {
  return {
    accessorKey: 'rankInGuild',
    id: 'rankInGuild',
    size: 90,
    ...genericSorting<wowaudit.CharacterReference>(
      'alphanumeric',
      'Rank',
      false,
    ),
    cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
      const label = guildRankLabel(
        info.row.original.rankInGuild,
        guildRanks,
        'GM',
      )

      return (
        <span className='block truncate' title={label}>
          {label}
        </span>
      )
    },
  }
}

export const refreshedAt = (
  t: TFunction,
  source: 'user' | 'guild',
  action?: (character: wowaudit.CharacterReference) => JSX.Element,
) => {
  return {
    accessorKey: 'refreshedAt',
    id: 'refreshedAt',
    size: 175,
    ...genericSorting<wowaudit.CharacterReference>(
      'alphanumeric',
      <div className='text-right'>Details updated</div>,
      true,
    ),
    align: 'right',
    cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
      return (
        <div className='text-right'>
          {info.row.original.gdprStatus === 'exists' ? (
            <Timestamp time={info.row.original.refreshedAt} />
          ) : (
            <div className='flex items-center justify-end gap-2'>
              {source === 'user' ? (
                <Popover>
                  <PopoverTrigger asChild className='flex items-center gap-2'>
                    <Edit
                      size={14}
                      className='cursor-pointer opacity-70 hover:opacity-100'
                    />
                  </PopoverTrigger>
                  <PopoverContent
                    align='end'
                    className='text-sm text-center w-36'
                  >
                    <div className='mb-2'>{t('shared.mark-as')}</div>
                    <div className='flex flex-col gap-1'>
                      {action ? action(info.row.original) : null}
                    </div>
                  </PopoverContent>
                </Popover>
              ) : null}
              <span className='italic opacity-70'>
                {t(
                  `profile.character-gdpr-status.${info.row.original.gdprStatus}`,
                ).toLowerCase()}
              </span>
            </div>
          )}
        </div>
      )
    },
  }
}

export const links = {
  header: 'Links',
  accessorKey: 'links',
  id: 'links',
  size: 95,
  cell: (info: CellContext<wowaudit.CharacterReference, unknown>) => {
    return (
      <div className='flex gap-2 items-center'>
        <CharacterExternalIconLink
          type='blizzard'
          backendUrl={info.row.original.blizzardUrl}
        />
        <CharacterExternalIconLink
          type='wcl'
          className='w-5 min-w-5'
          backendUrl={info.row.original.wclUrl}
        />
        <CharacterExternalIconLink
          type='raiderio'
          backendUrl={info.row.original.raiderioUrl}
        />
      </div>
    )
  },
}
